// src/redux/modules/auth/services/authService.ts
import { tokenService } from './tokenService';
import { AUTH_MESSAGES } from '@/rtkQuery/config/authApi.config';
import type { ChangePasswordRequest, LoginRequest, RegisterRequest, RegisterResponse, ResetPasswordRequest } from '../../models/auth.model';
import { handleApiResponse } from '@/rtkQuery/lib';

export class AuthService {
    async logout() {
        const { store } = await import('@/rtkQuery/store');
        const { baseApi } = await import('@/rtkQuery/features/services/baseApi');

        tokenService.clearTokens();
        store.dispatch(baseApi.util.resetApiState());

        handleApiResponse({
            result: { isSuccess: true },
            customSuccessMessage: AUTH_MESSAGES.LOGOUT_SUCCESS,
        });
    }
    static initializeAuthState() {
        return {
            accessToken: tokenService.getAccessToken(),
            refreshToken: tokenService.getRefreshToken(),
            isAuthenticated: tokenService.hasValidTokens(),
        };
    }

    async login(
        trigger: (payload: LoginRequest) => any,
        payload: LoginRequest,
        messages?: { success?: string }
    ) {
        try {
            const response = await trigger(payload).unwrap();
            tokenService.setTokens({
                accessToken: response.AccessToken,
                refreshToken: response.RefreshToken,
            });
            handleApiResponse({
                result: { isSuccess: true, data: response },
                customSuccessMessage: messages?.success || AUTH_MESSAGES.LOGIN_SUCCESS,
            });
            return { success: true, data: response };
        } catch (err: any) {
            const errorMessage = err?.message || err?.data?.message || AUTH_MESSAGES.LOGIN_ERROR;
            handleApiResponse({
                result: { isError: true, data: err?.data },
                customErrorMessage: errorMessage,
            });
            return { success: false, error: errorMessage };
        }
    }

    async register(
        trigger: (payload: RegisterRequest) => any,
        payload: RegisterRequest,
        messages?: { success?: string }
    ): Promise<{ success: true; data: RegisterResponse } | { success: false; error: string }> {
        try {
            const response = await trigger(payload).unwrap();

            tokenService.setTokens({
                accessToken: response.AccessToken,
                refreshToken: response.RefreshToken,
            });

            handleApiResponse({
                result: { isSuccess: true, data: response },
                customSuccessMessage: messages?.success || AUTH_MESSAGES.REGISTER_SUCCESS,
            });

            return { success: true, data: response };
        } catch (err: any) {
            const descriptionError = err?.data?.Errors?.[0]?.Description;
            const fallbackMessage = err?.data?.Message || err?.message;
            const errorMessage = descriptionError || fallbackMessage || AUTH_MESSAGES.REGISTER_ERROR;

            handleApiResponse({
                result: { isError: true, data: err?.data },
                customErrorMessage: errorMessage,
            });

            return { success: false, error: errorMessage };
        }

    }


    async refreshToken(
        trigger: () => Promise<any>,
    ) {
        try {
            const refreshResult = await trigger();
            const data = refreshResult?.data || refreshResult;
            tokenService.setTokens({
                accessToken: data.AccessToken,
                refreshToken: data.RefreshToken,
            });
            return { success: true, data };
        } catch (err: any) {
            return {
                success: false,
                error: err?.data?.message || err?.message || AUTH_MESSAGES.GENERIC_ERROR,
            };
        }
    }

    async changePassword(
        trigger: (payload: ChangePasswordRequest) => any,
        payload: ChangePasswordRequest,
        messages?: { success?: string }
    ) {
        try {
            const response = await trigger(payload).unwrap();

            handleApiResponse({
                result: { isSuccess: true, data: response },
                customSuccessMessage: messages?.success || AUTH_MESSAGES.CHANGE_PASSWORD_SUCCESS,
            });

            return { success: true, data: response };
        } catch (err: any) {
            const errorMessage =
                err?.message || err?.data?.message || AUTH_MESSAGES.CHANGE_PASSWORD_ERROR;

            handleApiResponse({
                result: { isError: true, data: err?.data },
                customErrorMessage: errorMessage,
            });

            return { success: false, error: errorMessage };
        }
    }

    async resetPassword(
        trigger: (payload: ResetPasswordRequest) => any,
        payload: ResetPasswordRequest,
        messages?: { success?: string }
    ) {
        try {
            const response = await trigger(payload).unwrap();

            handleApiResponse({
                result: { isSuccess: true, data: response },
                customSuccessMessage: messages?.success || AUTH_MESSAGES.RESET_SUCCESS,
            });

            return { success: true, data: response };
        } catch (err: any) {
            const errorMessage = err?.Message || err?.message || err?.data?.message || AUTH_MESSAGES.RESET_ERROR;

            handleApiResponse({
                result: { isError: true, data: err?.data },
                customErrorMessage: errorMessage,
            });

            return { success: false, error: errorMessage };
        }
    }

    static isSessionValid(): boolean {
        return tokenService.hasValidTokens() && !tokenService.isAccessTokenExpired();
    }
}

